// Everything the A01 reference sheet DRAWS but no upstream stage COMPUTES.
//
// The site layer arrives as bars, roads, a flat list of stalls, sidewalks, two
// classes of green and the street accesses. A real "plan de situație" carries a
// great deal more on top of that geometry — tree canopies over the planting, an
// asphalt apron under each parking row with the planting breaks showing through
// it, give-way plates where a minor road meets a major one, painted direction
// arrows, a zebra at the street access, dimension chains, numbered boundary
// vertices. None of it exists in the document, and putting it there would mean a
// schema change for what is purely a drafting convention.
//
// So it is derived HERE, from the geometry that does arrive. Two rules hold
// throughout:
//
//   * DETERMINISM. Nothing may call `Math.random()`. The same document must
//     produce the same drawing on every render, or the on-screen plan and the
//     exported PDF disagree — and a tree that moves when you pan reads as a bug
//     in the solver rather than as decoration.
//   * METRES. Every number here is in drawing metres, the same space the
//     component draws in, because the PDF export path hands the SVG straight to
//     svg2pdf with a metre viewBox and no `non-scaling-stroke` support.
import { capetele, carosabil, dilata, type Sageata } from "./carosabil";
import type { Pt } from "../types";
import type { SiteBar, SiteCrossing, SiteRoad, SiteShape, SolverSpace } from "../client";
import { pointInPolygon } from "../rules";
import { numar } from "../../i18n";
import { Obstacole, distLaInel, orientarea, palcuri, pomiInPalcuri, zar, type Palc } from "./vegetatie";

// A FALLBACK only, for a producer that sends a bay as a centre and a heading
// with no footprint. When a footprint is there it wins — see `siteStalls`.
//
// 2,5 x 5,0 is what the surface tiler used, and it is NOT the basement's:
// `plan/glyphs.tsx` draws a basement bay with the size the document gives it
// (`solver_geom.STALL_LEN`, 5,00 m, or 5,75 m parallel). The yard keeps its own
// constants: drawing it with the basement's once stretched every surface stall
// past the asphalt it was tiled onto.
export const SITE_STALL_W = 2.5;
export const SITE_STALL_D = 5.0;

// --------------------------------------------------------------------------- //
// small geometry helpers
// --------------------------------------------------------------------------- //

/** The stall convention, in one place. `deg` is an SVG rotation, so it turns
 *  clockwise in a y-down space: local +x (the stall's width, along its row) maps
 *  to `(cos, sin)` and local +y (its depth) to `(-sin, cos)`. The OUTWARD
 *  heading — the way a car leaves, and the way `SiteEntrance.deg` points — is
 *  local -y, i.e. `(sin, -cos)`. */
export const axes = (deg: number) => {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return { wx: c, wy: s, dx: -s, dy: c };      // width axis, depth axis
};

export const ringOf = (sh: SiteShape): number[][] =>
  Array.isArray(sh) ? sh : (sh?.poly ?? []);
export const holesOf = (sh: SiteShape): number[][][] =>
  Array.isArray(sh) ? [] : (sh?.holes ?? []);

export function centroid(poly: number[][]): Pt {
  let x = 0, y = 0;
  for (const p of poly) { x += p[0]; y += p[1]; }
  const n = poly.length || 1;
  return [x / n, y / n];
}

export function bboxOf(poly: number[][]) {
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys),
           x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** Distance from a point to a segment — the primitive behind "how much room is
 *  there here", which is what decides whether a tree fits and how wide its
 *  canopy may be. */
function distToSeg(px: number, py: number,
                   ax: number, ay: number, bx: number, by: number): number {
  const vx = bx - ax, vy = by - ay;
  const len2 = vx * vx + vy * vy;
  const t = len2 <= 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / len2));
  return Math.hypot(px - (ax + t * vx), py - (ay + t * vy));
}

/** Clearance from a point to the nearest edge of a ring (or of any of its
 *  holes). Used as "the radius that still fits here". */
export function clearance(x: number, y: number, rings: number[][][]): number {
  let best = Infinity;
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      const d = distToSeg(x, y, a[0], a[1], b[0], b[1]);
      if (d < best) best = d;
    }
  }
  return best;
}

/** Inside the surface: inside the outer ring and outside every hole. */
export function insideShape(x: number, y: number, sh: SiteShape): boolean {
  const ring = ringOf(sh);
  if (ring.length < 3 || !pointInPolygon(x, y, ring as Pt[])) return false;
  for (const h of holesOf(sh)) {
    if (h.length >= 3 && pointInPolygon(x, y, h as Pt[])) return false;
  }
  return true;
}

/** mulberry32 — a three-line PRNG with a 32-bit seed. Deterministic by
 *  construction, which is the whole point: see the header note. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable 32-bit seed for a polygon: its own coordinates. Two identical
 *  documents seed identically; moving a building reseeds only its own green. */
function seedOf(ring: number[][], salt: number): number {
  let h = 2166136261 ^ salt;
  for (const p of ring) {
    h = Math.imul(h ^ Math.round(p[0] * 16), 16777619);
    h = Math.imul(h ^ Math.round(p[1] * 16), 16777619);
  }
  return h >>> 0;
}

// --------------------------------------------------------------------------- //
// parking rows and their asphalt apron
// --------------------------------------------------------------------------- //

/** One surface bay, with its geometry RECOVERED rather than believed. */
export interface SiteStall {
  /** Index into `site.stalls` — what a `stall` Selection carries. */
  i: number;
  cx: number; cy: number;
  /** Glyph rotation, normalised to [0, 180): local +y lies along the bay's LONG
   *  axis. Which of the two directions the car actually faces is not knowable
   *  from a rectangle, so it is resolved per row against the aisle. */
  deg: number;
  /** Across the row, and along it. `d` is the long side — the car's length,
   *  whether the bay is perpendicular (2.5 x 5.0) or parallel (2.0 x 6.0). */
  w: number; d: number;
  kind?: string;
  /** `false` = loc roșu (încalcă o regulă, `SolverSpace.valid`); lipsă = valid. */
  valid?: boolean;
  /** Numărul de pe plăcuță, când producătorul l-a scris (`SolverSpace.n`). */
  n?: number;
}

export interface StallRun {
  /** The bays of this row, ordered along it. */
  stalls: SiteStall[];
  /** Indices into `site.stalls`, ordered along the row. */
  idx: number[];
  /** The row's heading, with the car facing resolved against the aisle. */
  deg: number;
  /** The apron pad, as a rotated rectangle in drawing metres. */
  cx: number; cy: number; w: number; h: number;
  /** Stall pitch positions along the run, for the divider lines and the
   *  dimension chain: distance from the pad's leading edge. */
  cuts: number[];
}

/** Recover what a bay actually is from the ring the producer drew.
 *
 *  `deg` is NOT trusted. The site-plan generator writes a correctly rotated
 *  `poly` and leaves `deg` at 0 on every bay: on the scheme this was found on,
 *  all 101 bays declared 0 while their rings sat at a bearing of 176 degrees.
 *  Believing `deg` drew a rotated scheme's parking axis-aligned — 94 pairs of
 *  bays overlapping on a document whose own geometry has none.
 *
 *  So when a ring is there it is the authority, and `deg` is the fallback for
 *  the producers that send a centre and a heading and no footprint. */
/** Inelul unui loc de parcare, așa cum e DESENAT: `SiteBay` îl pune ca
 *  `translate(cx, cy) rotate(deg)` peste un dreptunghi `w × d`.
 *
 *  Cotele îl iau de aici, nu din `SolverSpace.poly`, fiindcă un loc poate
 *  ajunge fără poligon și atunci e recuperat din `cx/cy/deg` — desenat, deci
 *  cotabil. O cotă care măsoară altceva decât ce se vede pe planșă e mai rea
 *  decât una lipsă. */
export function inelLoc(s: SiteStall): number[][] {
  const t = (s.deg * Math.PI) / 180;
  const co = Math.cos(t), si = Math.sin(t);
  const w = s.w / 2, d = s.d / 2;
  return ([[-w, -d], [w, -d], [w, d], [-w, d]] as const)
    .map(([x, y]) => [s.cx + x * co - y * si, s.cy + x * si + y * co]);
}

export function siteStalls(stalls: SolverSpace[] | undefined): SiteStall[] {
  const out: SiteStall[] = [];
  (stalls ?? []).forEach((s, i) => {
    if (!s) return;
    const kind = s.kind;
    // numai `false` se poartă mai departe: lipsa rămâne lipsă, ca desenul vechi
    const valid = s.valid === false ? { valid: false } : {};
    const nr = typeof s.n === "number" ? { n: s.n } : {};
    const ring = (s.poly ?? []).slice();
    if (ring.length > 1 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]) ring.pop();

    if (ring.length === 4 && ring.every((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]))) {
      const edges = ring.map((p, k) => {
        const q = ring[(k + 1) % 4];
        return { len: Math.hypot(q[0] - p[0], q[1] - p[1]),
                 ax: Math.atan2(q[1] - p[1], q[0] - p[0]) };
      });
      // Opposite edges of a rectangle are equal, so the two distinct lengths are
      // edges 0 and 1; the longer one is the car's length.
      const long = edges[0].len >= edges[1].len ? edges[0] : edges[1];
      const d = Math.max(edges[0].len, edges[1].len);
      const w = Math.min(edges[0].len, edges[1].len);
      if (d > 0.5 && w > 0.5) {
        const cx = (ring[0][0] + ring[1][0] + ring[2][0] + ring[3][0]) / 4;
        const cy = (ring[0][1] + ring[1][1] + ring[2][1] + ring[3][1]) / 4;
        // local +y = (-sin, cos) must lie along the long axis
        const ux = Math.cos(long.ax), uy = Math.sin(long.ax);
        let deg = (Math.atan2(-ux, uy) * 180) / Math.PI;
        deg = ((deg % 180) + 180) % 180;      // the two directions are one row
        out.push({ i, cx, cy, deg, w, d, kind, ...valid, ...nr });
        return;
      }
    }
    if (!Number.isFinite(s.cx) || !Number.isFinite(s.cy) || !Number.isFinite(s.deg)) return;
    out.push({ i, cx: s.cx, cy: s.cy, deg: ((s.deg % 180) + 180) % 180,
               w: SITE_STALL_W, d: SITE_STALL_D, kind, ...valid, ...nr });
  });
  return out;
}

/** Group the flat stall list back into the rows it was tiled as.
 *
 *  The surface tiler emitted `module → slot → side`, so ADJACENT INDICES ARE THE
 *  TWO OPPOSING ROWS at the same position along the aisle, not neighbours in one
 *  row — same-row neighbours are two apart, and that only holds while both sides
 *  are present. Nothing in the payload says which row a stall belongs to.
 *
 *  So the rows are recovered geometrically instead: stalls that share a heading
 *  AND a position across that heading are one row, whatever order they arrived
 *  in. That also survives the ingest path, where surface stalls come from a DWG
 *  and were never tiled in rows at all. */
export function stallRuns(ss: SiteStall[], roads: RoadAxis[] = []): StallRun[] {
  if (!ss.length) return [];
  // A LOCAL ORIGIN, and it is not cosmetic. Documents arrive in the national
  // grid, so a bay sits at about (326 000, 583 000); the bucket key is that
  // point projected across the row, and projecting a coordinate of that size
  // amplifies any wobble in the recovered heading by its own magnitude. A
  // hundredth of a degree — which is all the 3-decimal rounding in the payload
  // costs — moved `v` by some ninety metres, so every bay bucketed alone: 101
  // bays became 42 rows, 24 of them a single bay drawing its own apron.
  const ox = ss.reduce((t, s) => t + s.cx, 0) / ss.length;
  const oy = ss.reduce((t, s) => t + s.cy, 0) / ss.length;

  const buckets = new Map<string, SiteStall[]>();
  for (const s of ss) {
    // Half a degree and a quarter of a metre: tight enough that two rows never
    // merge, loose enough that one row does not split on the rounding a
    // producer's coordinates arrive with. A split row is worse than a merged
    // one — it draws two aprons over each other. The SNAPPED heading is what
    // projects, so every bay in a row measures against the same axis.
    const snapped = Math.round(s.deg * 2) / 2;
    const { dx, dy } = axes(snapped);
    const v = Math.round(((s.cx - ox) * dx + (s.cy - oy) * dy) * 4) / 4;
    const key = `${snapped.toFixed(1)}|${v}`;
    (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(s);
  }

  const runs: StallRun[] = [];
  for (const [key, group0] of buckets) {
    const deg0 = Number(key.split("|")[0]);
    const { wx, wy, dx, dy } = axes(deg0);
    // Also measured from the local origin, for the same reason.
    const along = (s: SiteStall) => (s.cx - ox) * wx + (s.cy - oy) * wy;
    group0.sort((a, b) => along(a) - along(b));
    const pitch = group0[0]?.w ?? SITE_STALL_W;
    const depth = group0[0]?.d ?? SITE_STALL_D;

    // Split into contiguous runs. A planting break is ONE stall width wide
    // (the tiler left a planting break every eight bays), so the apron has to
    // bridge a gap of two pitches — the reference paints one continuous pad and
    // lets the green show through it — but must NOT bridge the gap across an
    // aisle to the next module.
    const BRIDGE = pitch * 2.6;
    let group: SiteStall[] = [];
    const flush = () => {
      if (!group.length) return;
      const u0 = along(group[0]) - group[0].w / 2;
      const u1 = along(group[group.length - 1]) + group[group.length - 1].w / 2;
      // Averaged across the group, not taken from its first member: a row whose
      // bays wobble by a centimetre would otherwise hang its whole apron off
      // whichever bay happened to sort first.
      const v = group.reduce((t, s) => t + (s.cx - ox) * dx + (s.cy - oy) * dy, 0)
        / group.length;
      const mu = (u0 + u1) / 2;
      const cx = ox + mu * wx + v * dx, cy = oy + mu * wy + v * dy;
      // Which way the cars face. A rectangle cannot say — `deg0` and `deg0+180`
      // draw the same pad — so it is settled the way the reference settles it:
      // the nose points AWAY from the aisle the row is served by. Without this
      // both sides of a double-loaded bay face the same way, which is the one
      // thing about a parking row that reads as wrong at a glance.
      const face = facing(cx, cy, deg0, roads);
      runs.push({
        stalls: group.map((s) => ({ ...s, deg: face })),
        idx: group.map((s) => s.i), deg: face,
        cx, cy, w: u1 - u0, h: depth,
        cuts: group.map((s) => along(s) - u0),
      });
      group = [];
    };
    for (const s of group0) {
      if (group.length && along(s) - along(group[group.length - 1]) > BRIDGE) flush();
      group.push(s);
    }
    flush();
  }
  return runs;
}

/** `deg` or `deg+180`, whichever points the car away from the nearest aisle.
 *  With no roads to judge against, the heading is left as it came. */
function facing(cx: number, cy: number, deg: number, roads: RoadAxis[]): number {
  let best: RoadAxis | null = null, bestD = Infinity;
  for (const r of roads) {
    const d = distToSeg(cx, cy, r.x1, r.y1, r.x2, r.y2);
    if (d < bestD) { bestD = d; best = r; }
  }
  if (!best || bestD > 30) return deg;
  // The outward heading (the nose) is local -y = (sin, -cos).
  const a = (deg * Math.PI) / 180;
  const nx = Math.sin(a), ny = -Math.cos(a);
  // Toward the road, from the row's centre.
  const t = Math.max(0, Math.min(1,
    ((cx - best.x1) * (best.x2 - best.x1) + (cy - best.y1) * (best.y2 - best.y1)) /
    (((best.x2 - best.x1) ** 2 + (best.y2 - best.y1) ** 2) || 1)));
  const rx = best.x1 + t * (best.x2 - best.x1) - cx;
  const ry = best.y1 + t * (best.y2 - best.y1) - cy;
  return (nx * rx + ny * ry) > 0 ? (deg + 180) % 360 : deg;
}

// --------------------------------------------------------------------------- //
// trees
// --------------------------------------------------------------------------- //

export interface Tree { x: number; y: number; r: number; rot: number; blades: number }

/** Scatter canopies over a planted surface.
 *
 *  A jittered lattice rather than a uniform random spray: real planting is laid
 *  out on a rhythm and a spray clumps. Each canopy is then sized to the room it
 *  actually has — `clearance` to the nearest edge — which is what makes the same
 *  routine produce a 3 m crown in an open lawn and the small single tree the
 *  reference puts in a 2.5 m planting island, with no special case for either.
 *
 *  `step` is the lattice pitch; `salt` separates the two green classes so deep
 *  green and over-slab green do not scatter identically where they abut. */
export function scatterTrees(shapes: SiteShape[] | undefined, salt: number,
                             opts: { step?: number; minR?: number; maxR?: number } = {}): Tree[] {
  const step = opts.step ?? 6.5;
  const minR = opts.minR ?? 1.15;
  const maxR = opts.maxR ?? 3.6;
  const out: Tree[] = [];
  for (const sh of shapes ?? []) {
    const ring = ringOf(sh);
    if (ring.length < 3) continue;
    const rings = [ring, ...holesOf(sh)];
    const b = bboxOf(ring);
    const rand = rng(seedOf(ring, salt));
    // Lattice origin snapped to the grid so it does not shift with the bbox.
    const i0 = Math.floor(b.x0 / step), i1 = Math.ceil(b.x1 / step);
    const j0 = Math.floor(b.y0 / step), j1 = Math.ceil(b.y1 / step);
    // A pathological ring (a whole-lot annulus) would otherwise scatter tens of
    // thousands of candidates; the plan does not carry that many trees anyway.
    if ((i1 - i0) * (j1 - j0) > 20000) continue;
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        // Every candidate draws its three numbers whether or not it is kept, so
        // the sequence does not depend on which ones land inside.
        const jx = rand(), jy = rand(), jr = rand();
        const x = (i + 0.5 + (jx - 0.5) * 0.62) * step;
        const y = (j + 0.5 + (jy - 0.5) * 0.62) * step;
        if (!insideShape(x, y, sh)) continue;
        const room = clearance(x, y, rings);
        if (room < minR * 1.15) continue;
        // The size jitter goes INSIDE the clamp: applied after it, a 1.1x roll
        // pushed the crown past `maxR` and past the room it was measured for.
        const r = Math.min(maxR, room * 0.62 * (0.82 + jr * 0.28));
        if (r < minR) continue;
        out.push({ x, y, r, rot: jr * 360, blades: 8 + Math.floor(jx * 3) });
      }
    }
  }
  return out;
}

/** MARCAJELE CAROSABILULUI — axele punctate dintre benzi, ca polilinii în
 *  metri: aceeași derivare ca pe planșă (`carosabil`, cu aceleași argumente:
 *  axele drumurilor, rampele, lotul, asfaltul), plus racordurile în S
 *  (`road.path`). Macheta le vopsește pe asfalt; orice schimbare a drumurilor
 *  trece prin aceeași funcție, deci marcajul se reface singur. */
/** Aleea are sens unic (`Road.properties.two_way === false`). Un inel gol (un
 *  drum vechi, fără meta) e cu dublu sens. */
export const sensUnic = (r: SiteRoad): boolean => !Array.isArray(r) && r?.two_way === false;

/** Golul din axa punctată în jurul unei treceri de pietoni: liniuțele și
 *  săgețile de sens se opresc cu atât înaintea benzii (Oana, 31.10.2026). */
export const GOL_TRECERE_M = 1.5;

/** Marcajele de pe asfalt, în metri: liniuțele axelor cu dublu sens, săgețile de
 *  sens ale aleilor cu sens unic (centru + heading, convenția `RoadArrowMark`)
 *  și dungile zebrelor. */
export interface MarcajeSit { linii: Pt[][]; sageti: Sageata[]; zebre: DungaZebra[] }

/** Marcajele carosabilului ale MACHETEI, aceleași ca pe planșă: `SitePlanView`
 *  compune `carosabil` la fel (axa dreaptă, corpul și traseul curb al fiecărei
 *  alei, cu ACELAȘI inel în asfalt și în `corpuri`, golul la treceri) și
 *  taie zebrele la același asfalt. Macheta nu desena săgețile de sens și nici
 *  zebrele (Oana, 01.11.2026): o alee cu sens unic arăta ca una cu dublu sens
 *  fără liniuțe, iar trecerea lipsea cu totul. */
export function marcajeleSitului(site: { roads?: SiteRoad[]; ramps?: { poly: number[][] }[];
                                         crossings?: SiteCrossing[]; curburi?: boolean } | null | undefined,
                                 lot: readonly number[][] | null | undefined): MarcajeSit {
  const piese = (site?.roads ?? []).map((r) => {
    const corp = roadRing(r) ?? [];
    return { r, axa: roadAxis(r), corp: corp.length >= 3 ? corp : undefined,
             traseu: (!Array.isArray(r) && r.path && r.path.length > 2) ? (r.path as Pt[]) : undefined };
  });
  const cuAxa = piese.filter((p) => p.axa !== null);
  const roads = piese.map((p) => p.corp).filter((r): r is number[][] => !!r);
  const rampe = siteRamps(site?.ramps).map((r) => r.poly);
  const asfalt = [...roads, ...rampe];
  const treceri = site?.crossings ?? [];
  const zebre = treceri.flatMap((c) => zebraTrecere(c, asfalt));
  if (!cuAxa.length && !piese.some((p) => p.traseu)) return { linii: [], sageti: [], zebre };
  const car = carosabil(cuAxa.map((p) => p.axa!), rampe, (lot ?? []) as Pt[],
                        { curburi: site?.curburi !== false, asfalt,
                          corpuri: cuAxa.map((p) => p.corp),
                          goluri: treceri.map((c) => dilata(c.poly, GOL_TRECERE_M)),
                          trasee: piese.filter((p) => p.traseu)
                            .map((p) => ({ linie: p.traseu!, corp: p.corp, twoWay: !sensUnic(p.r) })) });
  return { linii: car.linii, sageti: car.sageti, zebre };
}

/** Doar liniuțele (polilinii în metri); v. `marcajeleSitului`. */
export function marcajeleCarosabilului(site: Parameters<typeof marcajeleSitului>[0],
                                       lot: readonly number[][] | null | undefined): number[][][] {
  return marcajeleSitului(site, lot).linii;
}

/** THE planting of a site, the same on the 2D sheet and in the 3D model — one
 *  function, so the two views cannot drift apart on a parameter. Both pass
 *  the same obstacles (`obstacolePlantatie`), so the trees kept away from the
 *  buildings are the same trees on both.
 *
 *  The deep green comes through as a narrow rim — a few metres between the
 *  property line and the ring road — so it is scattered on a tighter lattice and
 *  allowed smaller crowns than the lawns over the slab. Both lattices were
 *  widened on 01.10.2026 (4.0 → 6.0 m, 7.0 → 10.0 m): planted on the old pitch
 *  the green read as a hedge, with crowns touching all along; what a landscape
 *  plan shows is trees with lawn between them. */
export const PLANTATIE = {
  deep: { salt: 1, step: 6.0, minR: 0.8, maxR: 2.2 },
  over_slab: { salt: 2, step: 10.0, maxR: 3.6 },
} as const;

/** Cât de aproape de o clădire stă un pom: trunchiul la cel puțin 2 m de
 *  fațadă și de balcoane, iar coroana la cel puțin 0,5 m — un pom lipit de
 *  clădire își bagă coroana în ferestre și în balcoane. Unde coroana nu
 *  încape, pomul se micșorează; sub `coroanaMin`, nu se mai pune. */
export const POM_LANGA_CLADIRE = { trunchiMin: 2.0, margineCoroana: 0.5, coroanaMin: 0.8 } as const;

/** Ce ocolesc pomii: amprentele corpurilor și balcoanele care ies din ele. */
/** Ce nu primește pomi: clădirile și balcoanele lor, dar și DRUMURILE, aleile,
 *  locurile de parcare și rampele — pe placa parcajului verdele e desenat și
 *  sub ele, iar un pâlc verificat numai pe verde se așeza peste mașini. Aceeași
 *  listă pe planșă și pe machetă. */
export function obstacolePlantatie(site: { bars?: SiteBar[]; balconies?: number[][][];
                                          balconiiParter?: number[][][]; roads?: SiteRoad[];
                                          sidewalks?: SiteShape[]; stalls?: SolverSpace[];
                                          crossings?: { poly: number[][] }[];
                                          zones?: { poly: number[][] }[];
                                          ramps?: { poly: number[][] }[] } | null | undefined): number[][][] {
  // corpul fiecărui drum, cu găurile cusute: curtea din mijlocul unei alei-buclă primește pomi
  const drumuri = (site?.roads ?? []).map((r) => roadRing(r) ?? []);
  const locuri = siteStalls(site?.stalls).map((l) => {
    const a = (l.deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
    return [[-l.w / 2, -l.d / 2], [l.w / 2, -l.d / 2], [l.w / 2, l.d / 2], [-l.w / 2, l.d / 2]]
      .map(([u, v]) => [l.cx + u * c - v * sn, l.cy + u * sn + v * c]);
  });
  return [
    ...(site?.bars ?? []).map((b) => b?.pts ?? []),
    ...(site?.balconies ?? []),
    ...(site?.balconiiParter ?? []),
    ...drumuri,
    ...(site?.sidewalks ?? []).map((sh) => ringOf(sh)),
    // trecerile (nu mai sunt în `sidewalks`) și zonele exterioare (3.18): un
    // pom nu se pune pe zebră și nici pe locul de joacă sau pe platforma de gunoi
    ...(site?.crossings ?? []).map((c) => c.poly),
    ...(site?.zones ?? []).map((z) => z.poly),
    ...locuri,
    ...(site?.ramps ?? []).map((r) => r?.poly ?? []),
  ].filter((r) => r.length >= 3);
}

function departeDeCladiri(t: Tree, obstacole: number[][][]): Tree | null {
  const { trunchiMin, margineCoroana, coroanaMin } = POM_LANGA_CLADIRE;
  let d = Infinity;
  for (const ring of obstacole) {
    if (pointInPolygon(t.x, t.y, ring as Pt[])) return null;
    d = Math.min(d, clearance(t.x, t.y, [ring]));
  }
  if (d < trunchiMin) return null;
  const r = Math.min(t.r, d - margineCoroana);
  if (r < coroanaMin) return null;
  return r === t.r ? t : { ...t, r };
}

/** PÂLCURILE lotului (v. `vegetatie.ts`): puține și mari, PROPORȚIONALE cu
 *  terenul — cutia cea mare e `parte` din întinderea verdelui (între
 *  `laturaMin` și `laturaMax`), cu trepte în jos; fiecare loc ia cea mai mare
 *  cutie din care cel puțin `acoperire` cade pe verde liber, iar ce se
 *  plantează e doar partea aia: pâlcul urmează marginea terenului, drumurile
 *  și clădirile. Orientate ca blocurile, la `gol` unul de altul și la
 *  `margine` de obstacole. Pomii: grilă de 4,5 m tremurată tare, DEȘI lângă
 *  limita terenului (`densMargine`, la sub `langaLimita` m de ea) și RARI
 *  între blocuri (`densInterior`); tufe rare printre ei, fără gard viu. */
export const PALCURI = {
  parte: 0.22, laturaMin: 12, laturaMax: 60, trepte: [1, 0.7, 0.5] as readonly number[],
  pas: 6, gol: 12, margine: 1.0, acoperire: 0.45, sare: 7,
  pomi: { pas: 4.5, inset: 1.2, rMin: 1.6, rMax: 3.0 },
  langaLimita: 25, densMargine: 0.85, densInterior: 0.4,
} as const;

/** Laturile pâlcurilor pentru o întindere de verde de `L` metri (latura mare
 *  a cutiei lui), de la cea mai mare la cea mai mică. */
export function laturilePalcurilor(L: number): number[] {
  const mare = Math.max(PALCURI.laturaMin, Math.min(PALCURI.laturaMax, L * PALCURI.parte));
  const out = PALCURI.trepte.map((t) => Math.max(PALCURI.laturaMin, Math.round(mare * t)));
  return [...new Set(out)];
}

/** Pâlcurile și pomii lor — ce desenează planșa (pomii) și macheta (pomii și
 *  boscheții pâlcurilor). Pomii trec apoi prin regula clădirii
 *  (`POM_LANGA_CLADIRE`), ca oricare pom al lotului. */
/** Copacii pe care îi SPUNE documentul (`SiteLayer.trees`), în forma pomilor
 *  planșei: aceiași pe planșă și pe machetă, cu coroana cum o dă documentul.
 *  `rot` și `blades` din coordonate, ca la cei presărați (determinist). */
export function copaciiDocumentului(trees: readonly { x: number; y: number; r: number }[] | undefined): Tree[] {
  return (trees ?? []).map((t) => ({ x: t.x, y: t.y, r: t.r, rot: zar(t.x, t.y, 3) * 360,
                                      blades: 8 + Math.floor(zar(t.x, t.y, 4) * 3) }));
}

/** Cât aer lasă un pom presărat în jurul unuia din document: coroanele nu se ating. */
const AER_INTRE_COROANE_M = 0.5;

export function plantatiaLotului(green: { deep?: SiteShape[]; over_slab?: SiteShape[] } | undefined,
                                 obstacole: number[][][] = [],
                                 lot: readonly number[][] | null = null,
                                 fixe: readonly Tree[] = []): { palcuri: Palc[]; copaci: Tree[] } {
  const verde = [...(green?.deep ?? []), ...(green?.over_slab ?? [])]
    .map((sh) => ({ poly: ringOf(sh), holes: holesOf(sh) }))
    .filter((s) => s.poly.length >= 3);
  // clădirile fără margine: dreptunghiul pâlcului stă la `margine` de ele, iar
  // distanța trunchiului (`POM_LANGA_CLADIRE`) o ține fiecare pom, mai jos —
  // adunate, pâlcul nu mai încăpea în fâșia de 12 m dintre două blocuri
  const obst = new Obstacole(25);
  for (const r of obstacole) obst.inel(r, 0);
  const b = bboxOf(verde.flatMap((s) => s.poly));
  const laturi = laturilePalcurilor(Math.max(b.x1 - b.x0, b.y1 - b.y0));
  const p = palcuri(verde, obst, { laturi, pas: PALCURI.pas, gol: PALCURI.gol, margine: PALCURI.margine,
                                   acoperire: PALCURI.acoperire, sare: PALCURI.sare, deg: orientarea(obstacole) });
  // deși lângă limita terenului, rari înăuntru; fără lot, la mijloc
  const inel = lot && lot.length >= 3 ? lot : null;
  const densitate = (q: Palc) => (inel
    ? (distLaInel(q.cx, q.cy, inel as number[][]) < PALCURI.langaLimita ? PALCURI.densMargine : PALCURI.densInterior)
    : (PALCURI.densMargine + PALCURI.densInterior) / 2);
  const presarati = pomiInPalcuri(p, verde, obst, { ...PALCURI.pomi, densitate, sare: PALCURI.sare })
    .map((t): Tree => ({ ...t, rot: zar(t.x, t.y, 3) * 360, blades: 8 + Math.floor(zar(t.x, t.y, 4) * 3) }))
    .map((t) => (obstacole.length ? departeDeCladiri(t, obstacole) : t))
    .filter((t): t is Tree => t != null)
    // pomii documentului stau unde sunt; cei presărați se dau la o parte din jurul lor
    .filter((t) => !fixe.some((f) => Math.hypot(t.x - f.x, t.y - f.y) < t.r + f.r + AER_INTRE_COROANE_M));
  return { palcuri: p, copaci: [...fixe, ...presarati] };
}

export function plantatie(green: { deep?: SiteShape[]; over_slab?: SiteShape[] } | undefined,
                          obstacole: number[][][] = [], lot: readonly number[][] | null = null,
                          fixe: readonly Tree[] = []): Tree[] {
  return plantatiaLotului(green, obstacole, lot, fixe).copaci;
}

// --------------------------------------------------------------------------- //
// road centrelines, signage and markings
// --------------------------------------------------------------------------- //

export interface RoadAxis {
  x1: number; y1: number; x2: number; y2: number; w: number;
  /** From the document, not guessed from the width. */
  twoWay: boolean; kind: string;
}

/** Aria cu semn a unui inel (shoelace): semnul e orientarea. */
const ariaSemnata = (r: readonly number[][]) => {
  let a = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += r[j][0] * r[i][1] - r[i][0] * r[j][1];
  return a / 2;
};

/** UN SINGUR INEL dintr-un poligon cu găuri: fiecare gaură e «cusută» de
 *  exterior printr-o punte de lățime zero (poligon cu cheie), parcursă în sens
 *  invers exteriorului. Nu există bibliotecă de geometrie în arbore, iar tot ce
 *  consumă corpul unui drum (umplerea, tăierea axelor, obstacolele plantării,
 *  macheta) primește inele simple: pe un inel cu cheie testul punct-în-poligon
 *  (paritatea tăieturilor) și umplerea SVG «nonzero» dau exact poligonul cu
 *  găuri, iar distanța la muchii rămâne a muchiilor adevărate (puntea e dublă,
 *  deci de lățime zero). Fără găuri, inelul se întoarce neschimbat. */
export function inelCuGauri(poly: readonly number[][], holes?: readonly (readonly number[][])[]): number[][] {
  let inel = poly.slice();
  const sensExt = Math.sign(ariaSemnata(inel));
  for (const h0 of holes ?? []) {
    if (!h0 || h0.length < 3) continue;
    const h = Math.sign(ariaSemnata(h0)) === sensExt ? h0.slice().reverse() : h0.slice();
    // puntea: perechea de vârfuri (exterior, gaură) cea mai apropiată
    let bi = 0, bj = 0, bd = Infinity;
    for (let i = 0; i < inel.length; i++) {
      for (let j = 0; j < h.length; j++) {
        const d = Math.hypot(inel[i][0] - h[j][0], inel[i][1] - h[j][1]);
        if (d < bd) { bd = d; bi = i; bj = j; }
      }
    }
    const gaura = [...h.slice(bj), ...h.slice(0, bj), h[bj]];     // ocolul găurii, înapoi la punte
    inel = [...inel.slice(0, bi + 1), ...gaura, ...inel.slice(bi)];
  }
  return inel;
}

/** Normalise one road to a drawable ring. The backend mirrors the basement road
 *  dicts (p1/p2 centreline + `poly`); tolerate a bare ring too. Găurile
 *  corpului (aleea-buclă în jurul unei curți) intră în inel prin `inelCuGauri`. */
export function roadRing(r: SiteRoad): number[][] | null {
  if (Array.isArray(r)) return r.length >= 3 ? r : null;
  if (r.poly && r.poly.length >= 3) return r.holes?.length ? inelCuGauri(r.poly, r.holes) : r.poly;
  const ax = roadAxis(r);
  if (!ax) return null;
  const len = Math.hypot(ax.x2 - ax.x1, ax.y2 - ax.y1) || 1;
  const hw = ax.w / 2;
  const nx = (-(ax.y2 - ax.y1) / len) * hw, ny = ((ax.x2 - ax.x1) / len) * hw;
  return [[ax.x1 + nx, ax.y1 + ny], [ax.x2 + nx, ax.y2 + ny],
          [ax.x2 - nx, ax.y2 - ny], [ax.x1 - nx, ax.y1 - ny]];
}

/** Normalise one road to a centreline. The solver sends `p1`/`p2` plus a `poly`
 *  the ingest path may send a bare ring instead, which has no
 *  centreline at all — there the long axis of the ring's bbox stands in. */
export function roadAxis(r: SiteRoad): RoadAxis | null {
  // the S connector has no straight axis: its dashes follow its own polyline
  // (`SitePlanView`), and it takes no part in the T/corner/S pairing of `carosabil`
  if (!Array.isArray(r) && r?.path && r.path.length > 2) return null;
  // o piesă de asfalt fără axă (racordul, pavajul agentului) nu e o alee
  if (!Array.isArray(r) && r?.faraAxa) return null;
  const meta = Array.isArray(r)
    ? { twoWay: true, kind: "" }
    : { twoWay: r?.two_way !== false, kind: r?.kind ?? "" };
  if (!Array.isArray(r) && r?.p1 && r?.p2) {
    const [x1, y1] = r.p1, [x2, y2] = r.p2;
    if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
    return { x1, y1, x2, y2, w: r.w ?? 4.0, ...meta };
  }
  const ring = Array.isArray(r) ? r : (r?.poly ?? []);
  if (ring.length < 3) return null;
  const b = bboxOf(ring);
  const wide = b.x1 - b.x0 >= b.y1 - b.y0;
  return wide
    ? { x1: b.x0, y1: (b.y0 + b.y1) / 2, x2: b.x1, y2: (b.y0 + b.y1) / 2,
        w: (!Array.isArray(r) && r?.w) || (b.y1 - b.y0), ...meta }
    : { x1: (b.x0 + b.x1) / 2, y1: b.y0, x2: (b.x0 + b.x1) / 2, y2: b.y1,
        w: (!Array.isArray(r) && r?.w) || (b.x1 - b.x0), ...meta };
}

export type SignKind = "yield" | "noEntry" | "danger" | "ahead";
export interface SiteSign { x: number; y: number; deg: number; kind: SignKind }
export interface RoadMark { x: number; y: number; deg: number; kind: "straight" | "branch" }
export interface Crossing { x: number; y: number; deg: number; w: number; len: number }

const headingDeg = (dx: number, dy: number) =>
  (Math.atan2(dx, -dy) * 180) / Math.PI;      // inverse of (sin, -cos)

/** Where two centrelines cross, with both parameters inside the segments. */
function crossAt(a: RoadAxis, b: RoadAxis): Pt | null {
  const ax = a.x2 - a.x1, ay = a.y2 - a.y1;
  const bx = b.x2 - b.x1, by = b.y2 - b.y1;
  const den = ax * by - ay * bx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((b.x1 - a.x1) * by - (b.y1 - a.y1) * bx) / den;
  const u = ((b.x1 - a.x1) * ay - (b.y1 - a.y1) * ax) / den;
  if (t < -0.04 || t > 1.04 || u < -0.04 || u > 1.04) return null;
  return [a.x1 + t * ax, a.y1 + t * ay];
}

const len = (a: RoadAxis) => Math.hypot(a.x2 - a.x1, a.y2 - a.y1);

/** Signage and paint for the surface road network.
 *
 *  The basement has `derive.computeSignage`, and it was tempting to reuse it,
 *  but its clearances are tuned to a 5 m indoor aisle and its legality test
 *  knows only about stalls, rooms and ramps — outdoors the blockers are green,
 *  sidewalks and the property line. This is the same idea at surface scale:
 *  minor road yields to major at every junction, one-way connectors get an
 *  arrow, the street access gets a give-way and a zebra.
 *
 *  `blockers` are the building footprints; nothing is painted on them. */
export function siteSignage(roads: SiteRoad[] | undefined, lot: Pt[],
                            entries: number[][] | undefined,
                            blockers: number[][][]):
    { signs: SiteSign[]; marks: RoadMark[]; crossings: Crossing[] } {
  const axesList = (roads ?? []).map(roadAxis).filter((a): a is RoadAxis => a !== null);
  const signs: SiteSign[] = [], marks: RoadMark[] = [], crossings: Crossing[] = [];

  const free = (x: number, y: number) => {
    if (lot.length >= 3 && !pointInPolygon(x, y, lot)) return false;
    for (const b of blockers) {
      if (b.length >= 3 && pointInPolygon(x, y, b as Pt[])) return false;
    }
    return true;
  };
  const spaced = (list: { x: number; y: number }[], x: number, y: number, d: number) =>
    !list.some((p) => Math.hypot(p.x - x, p.y - y) < d);

  // --- junctions: the shorter leg yields ---------------------------------- //
  //
  // NU și la COLȚ: două alei care se termină una în alta sunt aceeași alee
  // care cotește, iar cine conduce pe ea nu trece printr-o intersecție
  // (Oana, 28.09.2026). Colțul îl recunoaște `carosabil.capetele`.
  const colturi = new Set(capetele(axesList)
    .filter((k) => k.fel === "colt" && k.j != null)
    .map((k) => `${Math.min(k.i, k.j!)}:${Math.max(k.i, k.j!)}`));
  const nodes: Pt[] = [];
  for (let i = 0; i < axesList.length; i++) {
    for (let j = i + 1; j < axesList.length; j++) {
      if (colturi.has(`${i}:${j}`)) continue;
      const p = crossAt(axesList[i], axesList[j]);
      if (!p) continue;
      if (nodes.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < 8)) continue;
      nodes.push(p);
      const minor = len(axesList[i]) <= len(axesList[j]) ? axesList[i] : axesList[j];
      if (len(minor) < 9) continue;
      // Stand the plate back from the mouth, on the near side of the minor leg.
      const dx = (minor.x2 - minor.x1) / len(minor), dy = (minor.y2 - minor.y1) / len(minor);
      const toStart = Math.hypot(minor.x1 - p[0], minor.y1 - p[1]);
      const sgn = toStart > len(minor) / 2 ? -1 : 1;   // point back down the leg
      const sx = p[0] + sgn * dx * 5.5 + -dy * (minor.w / 2 + 1.3);
      const sy = p[1] + sgn * dy * 5.5 + dx * (minor.w / 2 + 1.3);
      if (!free(sx, sy) || !spaced(signs, sx, sy, 6)) continue;
      signs.push({ x: sx, y: sy, deg: headingDeg(sgn * dx, sgn * dy), kind: "yield" });
    }
  }

  // --- painted arrows along the longer TWO-WAY roads ----------------------- //
  //
  // Only the two-way pairs are placed here. A one-way road gets its arrows
  // from `carosabil` (one every ~15 m, at least one, on the trimmed axis —
  // straight or curved), so it is skipped: two sources on the same road would
  // paint two arrows on top of each other (Oana, 30.10.2026).
  //
  // Spacing is judged at the STATION — the point on the centreline — not at each
  // arrow. Judging it per arrow is what silently made every two-way road one-way
  // on the drawing: the two arrows of a pair straddle the centreline half a lane
  // apart, 3 m on a 6 m aisle, so the second one always fell inside the
  // separation radius of the first and was dropped. All seven roads in the
  // scheme this was found on carry `two_way: true`, and not one of them got a
  // pair.
  const stations: { x: number; y: number }[] = [];
  for (const a of axesList) {
    if (!a.twoWay) continue;
    const L = len(a);
    if (L < 16) continue;
    const dx = (a.x2 - a.x1) / L, dy = (a.y2 - a.y1) / L;
    const n = Math.floor(L / 26);
    for (let k = 1; k <= n; k++) {
      const t = (k * L) / (n + 1);
      const cx = a.x1 + dx * t, cy = a.y1 + dy * t;
      if (!spaced(stations, cx, cy, 14)) continue;
      const off = a.twoWay ? a.w / 4 : 0;   // from the document, not the width
      const pair: RoadMark[] = [];
      for (const s of a.twoWay ? [1, -1] : [0]) {
        const x = cx + -dy * off * s, y = cy + dx * off * s;
        if (!free(x, y)) continue;
        // On a two-way road each half runs the other way.
        pair.push({ x, y, deg: headingDeg(dx * (s || 1), dy * (s || 1)),
                    kind: "straight" });
      }
      if (!pair.length) continue;
      marks.push(...pair);
      stations.push({ x: cx, y: cy });
    }
  }

  // --- the street access: give-way, no-entry and a zebra ------------------ //
  for (const e of entries ?? []) {
    if (!Array.isArray(e) || !Number.isFinite(e[0])) continue;
    const [ex, ey] = e;
    // The connector is whichever centreline starts or ends at the entry.
    let best: RoadAxis | null = null, bestD = Infinity;
    for (const a of axesList) {
      for (const p of [[a.x1, a.y1], [a.x2, a.y2]]) {
        const d = Math.hypot(p[0] - ex, p[1] - ey);
        if (d < bestD) { bestD = d; best = a; }
      }
    }
    if (!best || bestD > 12) continue;
    const L = len(best);
    if (L < 1) continue;
    // Point INTO the lot, whichever end of the connector met the entry.
    const flip = Math.hypot(best.x1 - ex, best.y1 - ey) > Math.hypot(best.x2 - ex, best.y2 - ey);
    const dx = ((flip ? -1 : 1) * (best.x2 - best.x1)) / L;
    const dy = ((flip ? -1 : 1) * (best.y2 - best.y1)) / L;
    const deg = headingDeg(dx, dy);
    const zx = ex + dx * 4.0, zy = ey + dy * 4.0;
    if (free(zx, zy)) {
      crossings.push({ x: zx, y: zy, deg, w: best.w, len: 3.0 });
    }
    for (const [s, kind] of [[1, "yield"], [-1, "noEntry"]] as const) {
      const px = ex + dx * 8.5 + -dy * (best.w / 2 + 1.4) * s;
      const py = ey + dy * 8.5 + dx * (best.w / 2 + 1.4) * s;
      if (free(px, py) && spaced(signs, px, py, 3)) {
        signs.push({ x: px, y: py, deg, kind });
      }
    }
  }
  return { signs, marks, crossings };
}

// --------------------------------------------------------------------------- //
// sheet furniture: dimension chains, boundary vertices, the public street
// --------------------------------------------------------------------------- //

export interface DimSpan {
  x1: number; y1: number; x2: number; y2: number; label: string;
  /** Punctele MĂSURATE, când linia de cotă e depărtată de ele (conturul
   *  lotului, lungimea unui corp): de aici pleacă liniile ajutătoare. Lipsesc
   *  când capetele liniei ating chiar lucrurile măsurate (rostul dintre două
   *  corpuri). */
  ex1?: Pt; ex2?: Pt;
}

/** Cotele PLANULUI DE SITUAȚIE se scriu în METRI, cu două zecimale: «18,00».
 *
 *  Planșa de referință le scria în centimetri, fără unitate («1800»), iar
 *  cerința lui Stefan (24.09.2026) a fost ca lungimea și lățimea blocului să
 *  se scrie CLAR — și un «4730» lângă un «47,30» ar fi cerut o legendă. Deci
 *  toate cotele planului, în aceeași unitate. Planul de arhitectură rămâne în
 *  centimetri (`cm`), ca planșele de nivel. */
export const metri = (m: number): string =>
  numar(m, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The reference writes every dimension in CENTIMETRES with no unit suffix —
 *  "550" under a 5.5 m stall pitch, "4773.8" along a boundary run. Trailing
 *  zeros are dropped so a round span reads "550" rather than "550.0". */
export const cm = (m: number): string => {
  const v = m * 100;
  return (Math.abs(v - Math.round(v)) < 0.05 ? Math.round(v) : Number(v.toFixed(1))).toString();
};

/** One chain per parking run, offset clear of the apron: the stall pitch,
 *  ticked at every divider, the way the reference dimensions a row. */
export function runChain(run: StallRun): DimSpan[] {
  const { wx, wy, dx, dy } = axes(run.deg);
  // On the NOSE side, which is the side away from the aisle. `run.deg` has its
  // facing resolved, so `+d` is the back of the bay — the aisle — and a chain
  // put there lands in the carriageway, and the two rows of a double-loaded bay
  // put theirs on top of each other in the same 6 m of road.
  const u0 = -run.w / 2, off = -(run.h / 2 + 1.6);
  const at = (u: number): Pt => [
    run.cx + (u0 + u) * wx + off * dx,
    run.cy + (u0 + u) * wy + off * dy,
  ];
  // Nasul locurilor — de acolo pleacă liniile ajutătoare ale lanțului.
  const at0 = (u: number): Pt => [
    run.cx + (u0 + u) * wx - (run.h / 2) * dx,
    run.cy + (u0 + u) * wy - (run.h / 2) * dy,
  ];
  const edges = [0, ...run.cuts.map((c, k) => c + (run.stalls[k]?.w ?? SITE_STALL_W) / 2)];
  const out: DimSpan[] = [];
  for (let i = 1; i < edges.length; i++) {
    const a = at(edges[i - 1]), b = at(edges[i]);
    out.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1], label: metri(edges[i] - edges[i - 1]),
               ex1: at0(edges[i - 1]), ex2: at0(edges[i]) });
  }
  return out;
}

export interface Vertex { i: number; x: number; y: number }

/** The boundary as the reference annotates it: every corner numbered, every run
 *  carrying its length. Corners that are all but collinear are skipped — a
 *  simplified survey ring can carry a 179.6° "corner" and numbering it says
 *  nothing. */
export function boundaryMarks(lot: Pt[], off = 3.5):
    { vertices: Vertex[]; runs: DimSpan[] } {
  const vertices: Vertex[] = [], runs: DimSpan[] = [];
  const n = lot.length;
  if (n < 3) return { vertices, runs };
  // A closed ring repeats its first point; do not number it twice.
  const closed = n > 3 && Math.hypot(lot[0][0] - lot[n - 1][0], lot[0][1] - lot[n - 1][1]) < 1e-6;
  const m = closed ? n - 1 : n;
  const c = centroid(lot.slice(0, m));
  let k = 1;
  for (let i = 0; i < m; i++) {
    const p = lot[i], a = lot[(i - 1 + m) % m], b = lot[(i + 1) % m];
    const ang = Math.abs(Math.atan2(b[1] - p[1], b[0] - p[0])
                       - Math.atan2(p[1] - a[1], p[0] - a[0]));
    const turn = Math.abs(((ang * 180) / Math.PI + 180) % 360 - 180);
    if (turn > 3) vertices.push({ i: k++, x: p[0], y: p[1] });
    const L = Math.hypot(b[0] - p[0], b[1] - p[1]);
    if (L <= 6) continue;
    // The run is dimensioned OUTSIDE the property, not on top of it: a value
    // plate straddling the boundary hides the line it is measuring.
    const ux = (b[0] - p[0]) / L, uy = (b[1] - p[1]) / L;
    let nx = -uy, ny = ux;
    const mx = (p[0] + b[0]) / 2, my = (p[1] + b[1]) / 2;
    if ((mx + nx - c[0]) ** 2 + (my + ny - c[1]) ** 2 < (mx - c[0]) ** 2 + (my - c[1]) ** 2) {
      nx = -nx; ny = -ny;
    }
    runs.push({ x1: p[0] + nx * off, y1: p[1] + ny * off,
                x2: b[0] + nx * off, y2: b[1] + ny * off, label: metri(L),
                ex1: [p[0], p[1]], ex2: [b[0], b[1]] });
  }
  return { vertices, runs };
}

/** The apron pad as a ring. The pads are re-used as a CLIP PATH — the planting
 *  breaks are re-drawn through them — and a `<polygon>` clip travels through the
 *  PDF exporter more predictably than a rotated `<rect>` does. */
export function runCorners(run: StallRun): number[][] {
  const { wx, wy, dx, dy } = axes(run.deg);
  const hw = run.w / 2, hh = run.h / 2;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([u, v]) =>
    [run.cx + u * wx + v * dx, run.cy + u * wy + v * dy]);
}

/** Building footprints as rings, for the "do not paint here" test. */
export const barRings = (bars: SiteBar[] | undefined): number[][][] =>
  (bars ?? []).map((b) => b?.pts ?? []).filter((r) => r.length >= 3);

// --------------------------------------------------------------------------- //
// ramps
// --------------------------------------------------------------------------- //

export interface SiteRamp {
  poly: number[][];
  slope?: number;
  /** The centreline, with the MOUTH end last: `a` is the deep end and `b` the
   *  opening at grade, so `a -> b` is the way the ramp climbs. */
  a: Pt; b: Pt;
  /** The centreline as a polyline from the mouth `b` to the deep end `a`. A
   *  straight ramp has two points; a ramp turned 90° inside its run (the
   *  architecture's «cotită»: tangent, quarter ring, straight) carries its own
   *  axis, and the dashes follow it rather than the chord. */
  path: Pt[];
  /** Down the ramp at the mouth, unit: the direction of the first leg. */
  u0: Pt;
  /** The deep-end wall, the edge of the outline nearest `a`. */
  deep: [Pt, Pt];
  cx: number; cy: number;
  /** Rotation for the grade text, so it reads ALONG the ramp and never upside
   *  down. */
  deg: number;
  /** Across the ramp — the annotation is sized and spaced from it, because a
   *  3,5 m ramp will not take the lettering a 5,5 m one does. */
  w: number;
  /** The ramp's OUTLINE, as an open polyline that stops at the mouth.
   *
   *  A ramp is not a closed box on a site plan: it has retaining walls down its
   *  two sides and an OPEN end where it meets the carriageway. Stroking the ring
   *  drew a line across that opening, straight over the road — the reference
   *  leaves it open and lets the road run through. */
  wall: number[][];
  /** Which way is UPHILL in the label's own frame, +1 or -1.
   *
   *  `deg` is turned by 180 when the text would otherwise read upside down, and
   *  that turn takes the grade arrow with it — so the arrow pointed downhill on
   *  every ramp whose run happened to face the wrong way. The flip is recorded
   *  here instead of being re-derived from an angle that no longer says which
   *  end is which. */
  up: 1 | -1;
  /** How far the MOUTH reaches into the ramp from the opening, in metres: the
   *  stretch at 5 % before the real grade (6 m on a generated ramp, the rule
   *  the -1 sheet already draws — Oana, 29.09.2026). 0 when the document has
   *  no mouth; nothing is invented. */
  gura: number;
}

/** Everything the grade annotation needs, from the ring the producer drew.
 *
 *  The reference writes the grade twice over — once as a percentage, once in
 *  degrees, both turned to read up the ramp — with an arrow beside them for
 *  which way it climbs. The arrow is the part that needs data rather than
 *  drafting: `Ramp.mouth` is the opening at grade, so the ramp rises TOWARD it,
 *  and without it the direction would be a coin toss. */
/** Length of a polyline. */
function pathLength(path: Pt[]): number {
  let L = 0;
  for (let i = 1; i < path.length; i++) L += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
  return L;
}

/** Point and unit direction of a polyline at length `s` from its start,
 *  clamped to its ends. */
export function pathAt(path: Pt[], s: number): { p: Pt; u: Pt } {
  let rest = Math.max(0, s);
  for (let i = 1; i < path.length; i++) {
    const dx = path[i][0] - path[i - 1][0], dy = path[i][1] - path[i - 1][1];
    const l = Math.hypot(dx, dy);
    if (l < 1e-9) continue;
    if (rest <= l || i === path.length - 1) {
      const k = Math.min(1, rest / l);
      return { p: [path[i - 1][0] + dx * k, path[i - 1][1] + dy * k], u: [dx / l, dy / l] };
    }
    rest -= l;
  }
  return { p: [path[0][0], path[0][1]], u: [1, 0] };
}

export function siteRamps(ramps: { poly: number[][]; slope?: number; mouth?: number[][];
                                   width_m?: number; axisPath?: number[][] }[] | undefined): SiteRamp[] {
  const out: SiteRamp[] = [];
  for (const r of ramps ?? []) {
    const ring = (r?.poly ?? []).filter(
      (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
    // A closed ring repeats its first point.
    const q = ring.length > 1 &&
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1] ? ring.slice(0, -1) : ring;
    if (q.length < 4) continue;

    // The centreline joins the midpoints of the two SHORT sides.
    const mid = (i: number, j: number): Pt =>
      [(q[i][0] + q[j][0]) / 2, (q[i][1] + q[j][1]) / 2];
    const e = (i: number) => Math.hypot(q[(i + 1) % q.length][0] - q[i][0],
                                        q[(i + 1) % q.length][1] - q[i][1]);
    const shortFirst = e(0) <= e(1);
    let a: Pt = shortFirst ? mid(0, 1) : mid(1, 2);
    let b: Pt = shortFirst ? mid(2, 3) : mid(3, 0);

    // Point the centreline at the mouth: it is the high end.
    const m = (r.mouth ?? []).filter(
      (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
    if (m.length >= 3) {
      const mc = centroid(m);
      if (Math.hypot(a[0] - mc[0], a[1] - mc[1]) <
          Math.hypot(b[0] - mc[0], b[1] - mc[1])) { const t = a; a = b; b = t; }
    }
    // The ramp's own axis, when it carries one (the turned ramp): mouth first,
    // deep end last. It replaces the chord between the short sides, which on
    // an L-shaped outline joins two edges that are not its ends at all.
    const ax = (r.axisPath ?? []).filter(
      (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1])) as Pt[];
    let path: Pt[] = [b, a];
    if (ax.length >= 2) {
      path = ax.map((p) => [p[0], p[1]] as Pt);
      if (m.length >= 3) {
        const mc = centroid(m);
        const d0 = Math.hypot(path[0][0] - mc[0], path[0][1] - mc[1]);
        const d1 = Math.hypot(path[path.length - 1][0] - mc[0], path[path.length - 1][1] - mc[1]);
        if (d1 < d0) path.reverse();
      }
      b = path[0]; a = path[path.length - 1];
    }
    const total = pathLength(path);
    const u0 = pathAt(path, 0).u;
    // The mouth's reach: its farthest vertex projected on the first leg, from
    // the opening toward the deep end.
    let gura = 0;
    if (m.length >= 3 && total > 0) {
      for (const p of m)
        gura = Math.max(gura, (p[0] - b[0]) * u0[0] + (p[1] - b[1]) * u0[1]);
      gura = Math.min(gura, total);
    }

    // The label sits halfway along the axis and reads along the leg it is on.
    const mijloc = pathAt(path, total / 2);
    const cx = mijloc.p[0], cy = mijloc.p[1];
    // Text runs along the ramp, flipped when it would otherwise read upside down.
    // Kept in [-90, 90] rather than just turned by 180: adding 180 to a run that
    // already read 180 gave 360, which draws the same but is not a rotation
    // anyone reading the value would expect.
    const raw = (Math.atan2(-mijloc.u[1], -mijloc.u[0]) * 180) / Math.PI;
    let deg = raw;
    let up: 1 | -1 = 1;
    if (raw > 90 || raw < -90) { deg = raw > 0 ? raw - 180 : raw + 180; up = -1; }
    // The mouth edge is the short side nearest the opening; the wall is the rest
    // of the ring, walked from the far end of that edge round to its near end.
    let mi = 0, best = Infinity;
    for (let i = 0; i < q.length; i++) {
      const j = (i + 1) % q.length;
      const d = Math.hypot((q[i][0] + q[j][0]) / 2 - b[0],
                           (q[i][1] + q[j][1]) / 2 - b[1]);
      if (d < best) { best = d; mi = i; }
    }
    // Walked round the WHOLE ring, not just four corners: the turned ramp's
    // outline has a few dozen vertices along its arc.
    const wall = Array.from({ length: q.length }, (_, k) => q[(mi + 1 + k) % q.length]);
    // The deep-end wall: the edge nearest the deep end of the axis.
    let di = 0; best = Infinity;
    for (let i = 0; i < q.length; i++) {
      const j = (i + 1) % q.length;
      const d = Math.hypot((q[i][0] + q[j][0]) / 2 - a[0], (q[i][1] + q[j][1]) / 2 - a[1]);
      if (d < best) { best = d; di = i; }
    }
    const deep: [Pt, Pt] = [q[di] as Pt, q[(di + 1) % q.length] as Pt];
    // Across the ramp: the document's width when it says one; the shortest
    // side of the ring is the 0,5 m step of a turned ramp, not its width.
    const w = Number.isFinite(r.width_m) && (r.width_m as number) > 0
      ? (r.width_m as number) : Math.min(e(0), e(1));

    out.push({ poly: q, slope: r.slope, a, b, path, u0, deep, cx, cy, deg, wall, w, up, gura });
  }
  return out;
}


// --------------------------------------------------------------------------- //
// which way is north — NOT HERE ANY MORE
// --------------------------------------------------------------------------- //
//
// `northingFirst` and `orientNorthUp` used to live here, and orienting the site
// layer is exactly what they did: the SITE layer, and nothing else. The storeys
// of the same document went to the canvas unturned, so a generated architecture
// drew with north to the RIGHT while its own plan de situație drew north up — a
// quarter turn between two views of one project.
//
// Which way is north is a rule of the FORMAT, not of a layer, so it lives at the
// mouth where a document becomes a drawing: `canonical.northUp`, whose Python
// twin is `canonical_model.read.north_up_xy`. By the time the site layer reaches
// this module it is already in the drawing frame, and nothing here may ask about
// `crs` again.

// --------------------------------------------------------------------------- //
// where a label fits
// --------------------------------------------------------------------------- //

/** The most central point INSIDE a footprint, and how much room it has.
 *
 *  The label used to sit at the average of the ring's vertices, sized to the
 *  bounding box. Both are wrong the moment a block is not a rectangle, and these
 *  are C- and L-shaped: the vertex average drifts toward whichever side has more
 *  corners and can land in the notch — outside the building — while the bounding
 *  box describes a space that is mostly not there, so the text was sized for
 *  room it did not have and ran over the walls.
 *
 *  This is the pole of inaccessibility: the interior point farthest from any
 *  edge. It is the visual centre of an awkward shape, and its distance to the
 *  boundary is exactly the room available — so the same number both places the
 *  text and bounds its size.
 *
 *  Found by a coarse grid and then refined, which is plenty at this scale and
 *  keeps it deterministic. */
export function labelSpot(poly: number[][]): { x: number; y: number; r: number } | null {
  const ring = (poly ?? []).filter(
    (p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
  if (ring.length < 3) return null;
  const b = bboxOf(ring);
  const w = b.x1 - b.x0, h = b.y1 - b.y0;
  if (!(w > 0) || !(h > 0)) return null;

  // On a long rectangle the farthest-from-the-edge point is not a point at all —
  // it is the whole centre ridge, every point of it equally far. Taking the
  // first one found puts the label at one END of the building. So ties go to
  // whichever is nearest the middle.
  const mx = (b.x0 + b.x1) / 2, my = (b.y0 + b.y1) / 2;
  const off = (x: number, y: number) => Math.hypot(x - mx, y - my);
  const EPS = 1e-6;
  let best = { x: mx, y: my, r: -1 };
  let step = Math.max(w, h) / 24;
  // Coarse sweep, then three passes that halve the step around the winner.
  for (let pass = 0; pass < 4; pass++) {
    const x0 = pass === 0 ? b.x0 + step / 2 : best.x - step * 2;
    const x1 = pass === 0 ? b.x1 : best.x + step * 2;
    const y0 = pass === 0 ? b.y0 + step / 2 : best.y - step * 2;
    const y1 = pass === 0 ? b.y1 : best.y + step * 2;
    for (let x = x0; x <= x1; x += step) {
      for (let y = y0; y <= y1; y += step) {
        if (!pointInPolygon(x, y, ring as Pt[])) continue;
        const r = clearance(x, y, [ring]);
        if (r > best.r + EPS ||
            (r > best.r - EPS && off(x, y) < off(best.x, best.y))) {
          best = { x, y, r };
        }
      }
    }
    step /= 2;
  }
  return best.r > 0 ? best : null;
}

// --------------------------------------------------------------------------- //
// trecerile de pietoni (3.18, §37)
// --------------------------------------------------------------------------- //

/** O dungă a zebrei: patrulater în metri de desen. */
export type DungaZebra = [Pt, Pt, Pt, Pt];

/** Dreptunghiul minim al unui inel, ca axă: direcția e a laturii inelului
 *  pe care dreptunghiul încadrat are aria cea mai mică (destul pentru o bandă
 *  dreaptă peste o alee); `axa` merge pe LATURA LUNGĂ, `w` e cea scurtă. */
function dreptunghiMinim(poly: number[][]): { axa: [Pt, Pt]; w: number } | null {
  const ring = (poly ?? []).filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
  if (ring.length < 3) return null;
  let best: { arie: number; ux: number; uy: number; u0: number; u1: number; v0: number; v1: number } | null = null;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 1e-9) continue;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
    for (const p of ring) {
      const u = p[0] * ux + p[1] * uy, v = -p[0] * uy + p[1] * ux;
      u0 = Math.min(u0, u); u1 = Math.max(u1, u); v0 = Math.min(v0, v); v1 = Math.max(v1, v);
    }
    const arie = (u1 - u0) * (v1 - v0);
    if (!best || arie < best.arie) best = { arie, ux, uy, u0, u1, v0, v1 };
  }
  if (!best) return null;
  const { ux, uy, u0, u1, v0, v1 } = best;
  const vm = (v0 + v1) / 2, um = (u0 + u1) / 2;
  const lung = u1 - u0 >= v1 - v0;
  // axa pe latura lungă, prin mijloc
  const axa: [Pt, Pt] = lung
    ? [[ux * u0 - uy * vm, uy * u0 + ux * vm], [ux * u1 - uy * vm, uy * u1 + ux * vm]]
    : [[ux * um - uy * v0, uy * um + ux * v0], [ux * um - uy * v1, uy * um + ux * v1]];
  return { axa, w: lung ? v1 - v0 : u1 - u0 };
}

/** Cât poate ieși o dungă peste muchia asfaltului: poligoanele sunt rotunjiri
 *  tesselate, iar bordura e exact muchia lor. */
const ZEBRA_PESTE_MUCHIE = 0.05;

/** Dungile zebrei unei treceri: albe, PERPENDICULARE pe `axis` (cele două
 *  capete ale trecerii, de-a latul aleii), pe toată lățimea `w` a trecerii.
 *  Dungi de `gros` (0,40 m) la pasul `pas` (0,80 m): mai discrete decât o
 *  zebră de stradă, cât o alee de incintă (Oana, 30.10.2026). Fără `axis`,
 *  dreptunghiul minim al lui `poly` dă axa și lățimea.
 *
 *  TĂIATE LA ASFALT: banda documentului iese 0,50 m peste bordură la fiecare
 *  capăt, ca să atingă trotuarul, dar dungile se vopsesc doar pe carosabil —
 *  cu `asfalt` (reuniunea drumurilor și rampelor) dungile se așază numai pe
 *  bucata axei care stă pe el, cea mai lungă dacă sunt mai multe. Pur, ca tot
 *  ce derivă planșa. */
export function zebraTrecere(c: { poly: number[][]; axis?: number[][]; w?: number },
                             asfalt: number[][][] = [], pas = 0.8, gros = 0.4): DungaZebra[] {
  let a: Pt | undefined, b: Pt | undefined, w = c.w;
  if (c.axis && c.axis.length >= 2) {
    a = [c.axis[0][0], c.axis[0][1]];
    b = [c.axis[c.axis.length - 1][0], c.axis[c.axis.length - 1][1]];
  }
  if (!a || !b || !(w && w > 0)) {
    const d = dreptunghiMinim(c.poly);
    if (!d) return [];
    if (!a || !b) [a, b] = d.axa;
    if (!(w && w > 0)) w = d.w;
  }
  const L0 = Math.hypot(b[0] - a[0], b[1] - a[1]);
  // un pas nul ar da o buclă fără capăt: fără pas, fără zebră
  if (!(L0 > 1e-6) || !(w > 0) || !(pas > 0) || !(gros > 0)) return [];
  const ux = (b[0] - a[0]) / L0, uy = (b[1] - a[1]) / L0;
  const nx = -uy, ny = ux;
  // bucata axei de pe asfalt: cea mai lungă rulare de probe (din 5 în 5 cm)
  // care cad pe un inel sau la `ZEBRA_PESTE_MUCHIE` de muchia lui
  let s0 = 0, s1 = L0;
  const inele = asfalt.filter((r) => r.length >= 3);
  if (inele.length) {
    const peAsfalt = (s: number) => {
      const x = a![0] + ux * s, y = a![1] + uy * s;
      return inele.some((r) => pointInPolygon(x, y, r as Pt[]) || clearance(x, y, [r]) <= ZEBRA_PESTE_MUCHIE);
    };
    const n = Math.max(1, Math.ceil(L0 / 0.05));
    let best: [number, number] | null = null, start: number | null = null;
    for (let i = 0; i <= n + 1; i++) {
      const ok = i <= n && peAsfalt((L0 * i) / n);
      if (ok && start === null) start = i;
      if (!ok && start !== null) {
        if (!best || i - 1 - start > best[1] - best[0]) best = [start, i - 1];
        start = null;
      }
    }
    if (!best) return [];
    s0 = (L0 * best[0]) / n; s1 = (L0 * best[1]) / n;
  }
  const L = s1 - s0;
  if (!(L > gros)) return [];
  // câte dungi încap la pasul exact, cu cel puțin `gol` la fiecare bordură ca
  // să nu înceapă lipite de ea; șirul se centrează pe bucata de asfalt
  const gol = Math.min(0.2, (L - gros) / 2);
  const n = Math.max(1, Math.floor((L - gros - 2 * gol) / pas) + 1);
  const deplasare = (L - ((n - 1) * pas + gros)) / 2;
  const out: DungaZebra[] = [];
  for (let i = 0; i < n; i++) {
    const d0 = s0 + deplasare + i * pas, d1 = d0 + gros;
    const q = (s: number, t: number): Pt => [a![0] + ux * s + nx * t, a![1] + uy * s + ny * t];
    out.push([q(d0, -w / 2), q(d1, -w / 2), q(d1, w / 2), q(d0, w / 2)]);
  }
  return out;
}
